import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable, of, EMPTY, concat, defer, from, throwError } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Lead } from '../shared/lead';
import { PostLead } from '../shared/post-lead';
import { FormResponse } from '../shared/form-response';
import { SelectedResponse } from '../shared/selected-response.model';
import { BeEnabledResponse } from '../shared/be-enabled-response';
import { GeneralResponse } from '../shared/general-response';
import { ThankYouQueryString } from '../shared/thank-you-query-string';
import { OfficeStatus } from '../shared/office-status';
import { PremiumQuestions } from '../shared/premium-questions';
import { PremiumAnswers } from '../shared/premium-answers';
import { LandingData } from '../shared/landing-data';
import { ActivatedRoute } from '@angular/router';
import { GetLocationResponse } from '../shared/get-location-response';
import { UntypedFormGroup } from '@angular/forms';
import { AnalyticsService } from './analytics.service';
import { NeustarScore } from '../shared/neustar-score';
import { ConsumerReviewsResponse } from '../shared/consumer-reviews-response';
import { catchError, finalize, map, mergeMap, retry, switchMap, take, tap } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { State } from '../shared/state';
import { StateRateDescription } from '../shared/state-rate-description.model';
import { PageInfo } from '../shared/page-info';
import { AdvertisedRateBucket, Rate } from '../shared/rate.model';
import { query } from '@angular/animations';
import {
  edofficeLegacyRatePreview,
  rateBucketKey,
  unavailableEffectiveRate,
  validatedEffectiveRate,
  EffectiveRateValidation,
} from './advertised-rate-context';
import {
  AD_ATTRIBUTION_STORAGE_KEY,
  buildConversationContext,
  parseConversationCookies,
  persistConversationAdAttribution,
  sanitizeConversationUrl,
} from './conversation-context';
import { addRateFinderBreadcrumb, captureRateFinderRequestFailure, ensureRateFinderSessionId } from './rate-finder-sentry';
import { ACTION_RETRY_DELAYS_MS, actionRetryEnv, actionRetryNotifier, pageShownAgain } from './action-retry';
import { SUBMIT_NO_ANSWER_RETRIES, SUBMIT_PROCESSING_WAITS, SUBMIT_PROCESSING_WAIT_MS, submitRetryPlan } from './submit-retry';
import { watchConnectionDrops } from './connection-drop';
import { pageDepartingSince, watchPageLeaving } from './page-leaving';
import { actionOutboxGuards, addToOutbox, defaultOutboxStore, flushOutbox, readOutbox, removeFromOutbox } from './action-outbox';

/** The body laravel-api's /status/location returns, with a 404, for a ZIP it has no record of. */
export const ZIP_NOT_FOUND_ERROR = 'Did not find ZIP code';

/** True only for that exact unknown-ZIP answer; any other failure (a 500, a timeout, another 404) is false. */
export function isZipNotFoundError(x: any): boolean {
  return !!(x && x.status === 404 && x.error && x.error.error === ZIP_NOT_FOUND_ERROR);
}

@Injectable()
export class FruHttpService {
  private wwwUrl: string;
  private adminUrl: string;
  private apiUrl: string;
  private officeStatus: Observable<OfficeStatus>;
  private lenderPhoneStatus: Observable<OfficeStatus>;
  private rateTableUrl: string;

  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private appConfig: AppConfigService,
  ) {
    this.wwwUrl = appConfig.wwwUrl;
    this.adminUrl = appConfig.adminUrl;
    this.rateTableUrl = appConfig.rateTableUrl;
    this.apiUrl = appConfig.fruApiUrl;
    watchPageLeaving();
    watchConnectionDrops();
    this.resumeActionOutbox();
  }

  getLead(id: string, type: string, adnetwork: string = 'consumer_campaign', consentForm: boolean = false, consentUpdate: boolean = false): Observable<Lead> {
    let url = '';

    if (type === 'abandonment') {
      url = `${this.wwwUrl}/ng_api/get_abandonment_lead/${id}`;
    } else {
      url = `${this.wwwUrl}/ng_api/get_lead/${id}?adnetwork=${adnetwork}`;
      if (consentForm) {
        url = `${url}&consent_form=1`;
      }
      if (consentUpdate) {
        url = `${url}&consent_update=1`;
      }
    }

    return this.http.get<Lead>(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getPremiumQuestions(id: string): Observable<PremiumQuestions> {
    return this.http.get<PremiumQuestions>(`${this.wwwUrl}/ng_api/get_premium_questions/${id}`).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getOfficeStatus(adnetwork: string = 'consumer_campaign'): Observable<OfficeStatus> {
    if (this.officeStatus) {
      return this.officeStatus;
    }
    const url = `${this.apiUrl}/status/office`;
    let params : any = {}
    if (adnetwork) {
      params.adnetwork = adnetwork;
    }
    params.domain = window.location.hostname;
    this.officeStatus = this.http.get<OfficeStatus>(url, { params: params} )
      .pipe(catchError(x => {
        return of({ answer: false, phone: null, phonefancy: null, adnetwork: null });
      }));

    return this.officeStatus;
  }

  getMedian() {
    const url: string = `${this.adminUrl}/public_api/median_price`;
    return this.http.get(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getLenderPhone(lender_id: string, adnetwork: string = 'consumer_campaign'): Observable<OfficeStatus> {
    // if (this.lenderPhoneStatus) {
    //  return this.lenderPhoneStatus;
    // }
    const url = `${this.wwwUrl}/ng_api/get_lender_phone/${lender_id}/${adnetwork}`;
    this.lenderPhoneStatus = this.http.get<OfficeStatus>(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
    return this.lenderPhoneStatus;
  }

  getBEstatus(lenderId: string, state: string = '', loanType: string = 'purchase') {
    let params = new HttpParams()
      .set('l', lenderId)
      .set('LoanPurpose', loanType);
    if (state != '') {
      params = params.append('state', state);
    }

    // deepcode ignore Ssrf: this method is run from the browser, not the server
    const url = `${this.wwwUrl}/ng_api/be_enabled?` + params.toString();
    // deepcode ignore Ssrf: this method is run from the browser, not the server
    return this.http.get<BeEnabledResponse>(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getLandingPageData(leadType: string, adnetwork: string, pageType: string = 'default') {
    let params = new HttpParams()
      .set('LoanPurpose', leadType)
      .set('adnetwork', adnetwork)
      .set('type', pageType);

    const url = `${this.wwwUrl}/ng_api/get_landing_data?` + params.toString();

    return this.http.get<LandingData>(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  submitLead(lead: PostLead): Observable<FormResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/lead_manager/submit`;
    // FRU-24081: a final submit whose answer never arrives is sent again with the same submission id, which the server uses to
    // give a repeat the first reply instead of a second lead. Each try is a breadcrumb; the form's own error handling runs only
    // once the tries are spent. If the page goes while one waits, the submit goes on a keepalive request.
    const plan = submitRetryPlan(lead);
    if (!plan.retry) {
      return this.http.post<FormResponse>(url, plan.body, httpOptions);
    }
    let noAnswer = 0;
    let processing = 0;
    // Waits for a visible, online page (after `pauseMs` at least). If the page goes while it waits, the submit is handed to a
    // keepalive request; the caller then hears nothing unless the page is shown again, when it gets the failure and can recover.
    // If no keepalive request could be started, or after ten minutes away, the form's own handling of a failed submit runs.
    const waitThen = (error: any, attempt: number, pauseMs: number) => {
      let handedOff = false;
      return concat(
        actionRetryNotifier(attempt, () => { handedOff = this.submitLeadOnUnload(url, plan.body); }, pauseMs),
        defer(() => (handedOff ? pageShownAgain().pipe(mergeMap(() => throwError(() => error))) : throwError(() => error))),
      );
    };
    return this.http.post<FormResponse>(url, plan.body, httpOptions).pipe(
      retry({
        count: SUBMIT_NO_ANSWER_RETRIES + SUBMIT_PROCESSING_WAITS,
        delay: (error) => {
          const status = error && error.status;
          if (status === 0 && noAnswer < SUBMIT_NO_ANSWER_RETRIES) {
            noAnswer++;
            addRateFinderBreadcrumb('submit_retry', { api: 'submitLead', attempt: noAnswer, reason: 'no_answer' });
            return waitThen(error, noAnswer, ACTION_RETRY_DELAYS_MS[Math.min(noAnswer, ACTION_RETRY_DELAYS_MS.length) - 1]);
          }
          const code = error && error.error && typeof error.error === 'object' ? error.error.error : null;
          if (status === 409 && code === 'submission_processing' && processing < SUBMIT_PROCESSING_WAITS) {
            processing++;
            addRateFinderBreadcrumb('submit_retry', { api: 'submitLead', attempt: processing, reason: 'processing' });
            return waitThen(error, processing, SUBMIT_PROCESSING_WAIT_MS);
          }
          return throwError(() => error);
        },
      }),
    );
  }

  /** FRU-24081: the same final submit on a request the browser finishes after the page is gone. */
  submitLeadOnUnload(url: string, body: any): boolean {
    try {
      const browserFetch = (window as any).fetch;
      if (typeof browserFetch !== 'function') { return false; }
      const request = browserFetch.call(window, url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: JSON.stringify(body),
        credentials: 'include',
        keepalive: true,
      });
      if (request && typeof request.catch === 'function') { request.catch(() => { /* the page is gone; nothing left to tell */ }); }
      return true;
    } catch (e) {
      return false;
    }
  }

  jornayaNotLoaded(encryptedLeadId, status: string = null): Observable<FormResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/jornaya_not_loaded`;

    return this.http.post<FormResponse>(url, {encryptedLeadId: encryptedLeadId, status: status}, httpOptions);
  }

  submitConsent(lead: PostLead): Observable<FormResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/onetoone`;

    return this.http.post<FormResponse>(url, lead, httpOptions);
  }

  // FRU-22871: first-party consent funnel diagnostics. The endpoint takes a JSON body (unlike the
  // form-encoded selected_manager endpoints, the payload carries a nested metadata object).
  // Deliberately a plain POST: telemetry errors are caught at the call site
  // (ConsentAttemptTelemetryService), never inside shared transport methods.
  consentAttemptEvent(payload: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/consent_attempt_event`;

    return this.http.post<any>(url, payload, httpOptions);
  }

  /**
   * FRU-23796: a Hope Direct Text Me Rates visitor's phone, kept by the server as a waiting row it turns into a Review lead
   * only if the visitor leaves. Returns the row's token for the later answers and the finished submit.
   */
  hopePhoneFirst(payload: any): Observable<{ status: string; token?: string }> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    return this.http.post<{ status: string; token?: string }>(`${this.adminUrl}/lead_manager/hope_phone_first`, payload, httpOptions);
  }

  /** FRU-25049: a resume link's waiting row answers (never names, email or phone), by the row's own token, sent in the body. */
  hopeResume(token: string): Observable<{ status: string; answers?: any; kind?: string }> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    return this.http.post<{ status: string; answers?: any; kind?: string }>(`${this.adminUrl}/lead_manager/hope_resume`, { token }, httpOptions);
  }

  /** FRU-25115: finishes a phone-call lead from its finish link: the missing answers and the phone step's consent, on the same lead. */
  hopePhoneCallFinish(body: any): Observable<{ status: string }> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    return this.http.post<{ status: string }>(`${this.adminUrl}/lead_manager/hope_phone_call_finish`, body, httpOptions);
  }

  /**
   * FRU-24923: a Hope Direct Email Me Rates visitor's email, kept by the server as a waiting row so a visitor who leaves after
   * the email isn't lost (it never becomes a Review lead). Returns the row's token, used like the phone row's.
   */
  hopeEmailFirst(payload: any): Observable<{ status: string; token?: string }> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    return this.http.post<{ status: string; token?: string }>(`${this.adminUrl}/lead_manager/hope_email_first`, payload, httpOptions);
  }

  /** FRU-23796: the answers a phone-first visitor gives after the phone, saved on their waiting row. */
  hopePhoneFirstUpdate(payload: any): Observable<any> {
    const httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true };
    return this.http.post<any>(`${this.adminUrl}/lead_manager/hope_phone_first_update`, payload, httpOptions);
  }

  consentAttemptEventOnUnload(payload: any): void {
    try {
      if (typeof fetch !== 'function') { return; }
      fetch(`${this.adminUrl}/selected_manager/consent_attempt_event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(payload),
      }).catch(() => { /* telemetry failures are non-blocking */ });
    } catch (e) { /* telemetry failures are non-blocking */ }
  }

  bestLender(bestId): Observable<any> {
    const url = `${this.wwwUrl}/ng_api/get_lender/${bestId}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  saveNotSubmitedLead(lead: PostLead, is_checked: number = 1): Observable<SelectedResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/submit?checked=${is_checked}`;

    return this.http.post(url, lead, httpOptions);
  }

  updateNotSubmitedLead(lead: PostLead): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/update`;

    return this.http.post(url, lead, httpOptions);
  }

  getDefaultLenders(lead: PostLead, is_checked: number = 1): Observable<SelectedResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/by_state?checked=${is_checked}`;

    return this.http.post(url, lead, httpOptions).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  setRocketPromptAnswer(clientId, nsLeadId, type, answer): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/prompt_answer`;
    let params = {nsLeadId: nsLeadId, rocket_client: clientId, prompt_type: type, answer: answer};

    return this.http.post(url, JSON.stringify(params), httpOptions);
  }

  setAllPromptAnswer(lenders, nsLeadId, type, answer): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/prompt_answer`;
    let params = {nsLeadId: nsLeadId, lenders: lenders, prompt_type: type, answer: answer};

    return this.http.post(url, JSON.stringify(params), httpOptions);
  }

  submitPremiumAnswers(premiumAnswers: PremiumAnswers): Observable<GeneralResponse> {
    return this.http.post<GeneralResponse>(`${this.wwwUrl}/ng_api/submit_premium_answers`, premiumAnswers, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    }).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  redirectThankYou(lead: Lead, response: FormResponse, qr: boolean = false, isHope: boolean = false) {
    console.log('redirectThankYou', lead, response);

    let qs = this.generateThankYouQueryString({
      leadType: lead.type,
      cashOut: lead.cashOut,
      creditRating: lead.creditRating,
      product: response.product,
      leadId: response.lead_id,
      dc: response.dc || 0,
      im: response.im || 0,
      prompt: response.prompt || 0,
      sac: response.sac,
      dnt: Number(qr),
      conversionType: response.conversion_type || '',
      hope: isHope ? 1 : 0,
    });

    const path = qr ? '/thank-you-qr' : '/thank-you/';
    this.analyticsService.trackRedirect(path);
    let tyUrl = this.wwwUrl;
    if (response.ty_domain) {
      tyUrl = 'https://' + response.ty_domain;
    }
    window.location.href = tyUrl + path + '?' + qs;
  }

  redirectThankYouNoLead(data: object) {
    // for premium questions
    let qs = this.generateThankYouQueryString(data);

    window.location.href = `${this.wwwUrl}/thank-you/?${qs}`;
  }

  generateThankYouQueryString(data: any): string {
    let query: ThankYouQueryString = {
      LoanPurpose: data.leadType || '',
      lc_referrer: 'FreeRateUpdate',
      cash_out: data.cashOut || '',
      credit_rating: data.creditRating || '',
      product_type: data.product || '',
      form: 'web',
      exclusive: 'multiple banks',
      form_version: 1,
      lead_id: data.leadId || '',
      dc: data.dc || 0,
      im: data.im || 0,
      prompt: data.prompt || 0,
      sac: data.sac || 0,
      dnt: data.dnt || 0,
      conversionType: data.conversionType || '',
    };

    // Keep the original/control thank-you URL byte-for-byte unchanged. Hope adds
    // this presentation-only marker so its receipt copy can be isolated after redirect.
    if (data.hope === 1 || data.hope === true) {
      query.hope = 1;
    }

    if (query.LoanPurpose != 'purchase' && query.LoanPurpose != 'refinance') {
      query.LoanPurpose = 'refinance'; //switch back LowRateFinder's types to be correct
    }

    let params = new HttpParams();

    for (let key in query) {
      params = params.set(key, query[key]);
    }

    return params.toString();
  }

  redirectMainForm() {
    window.location.href = `${this.wwwUrl}/mortgagerates/index.php?utm_source=consumer_campaign&utm_medium=email_form_redirect`;
  }
 
  /**
   * Redirect to the form with the specified type, and pass existing url params
   * @param type loan type
   */
  redirectForm(type: string): void {
    let qs: HttpParams = new HttpParams();
    for (let key in this.activatedRoute.snapshot.queryParams) {
      qs = qs.set(key, this.activatedRoute.snapshot.queryParams[key]);
    }
    qs = qs.set('LoanPurpose', type);

    let url: string = `${this.wwwUrl}/mortgagerates/final-step.php?${qs}`;
    window.location.href = url;
  }

  /** FRU-23942: the exact disclosure for a reviewed rate Hope shows outside the landing's own context (the returning visitor
   *  card's prior-product rate). Set only while that claim is on screen; the disclosure control opens it instead of the landing's.
   *  Kept on window, like the effective claim, because the page shell and Hope each hold their own instance of this service. */
  setAdDisclosureOverride(url: string | null): void {
    try { (window as any).fruAdDisclosureOverrideUrl = url || null; } catch (e) { /* non-fatal */ }
  }

  private adDisclosureOverride(): string | null {
    try {
      const url = (window as any).fruAdDisclosureOverrideUrl;
      return typeof url === 'string' && url ? url : null;
    } catch (e) { return null; }
  }

  /** FRU-24540: the address the disclosure control opens, computed without opening it. Same
   *  selection as adDisclosurePopup(), so an <a href> can carry exactly the address the click
   *  opens and the item is followable by a keyboard user, a crawler or an ad reviewer. */
  adDisclosureUrl(useEffectiveRateContext: boolean = false): string {
    const override = useEffectiveRateContext ? this.adDisclosureOverride() : null;
    if (override) {
      return override;
    }
    if (this.appConfig.effectiveAdvertisedRatesEnabled && useEffectiveRateContext) {
      const rate = this.currentEffectiveAdvertisedRate();
      if (rate && rate.status === 'ok' && rate.disclosure && rate.disclosure.url) {
        return rate.disclosure.url;
      }
      // No numeric effective claim is active, so keep the disclosure control
      // usable with the existing non-rate-specific destination.
    }

    let type = 'default';

    if (this.activatedRoute.snapshot.queryParams['type']) {
      type = this.activatedRoute.snapshot.queryParams['type'];
    }

    return `${this.wwwUrl}/advertising_disclosure.php?type=${type}`;
  }

  adDisclosurePopup(useEffectiveRateContext: boolean = false): void {
    this.analyticsService.trackGoal(63);
    window.open(this.adDisclosureUrl(useEffectiveRateContext), 'name', 'scrollbars=1,height=500,width=500');
  }

  licensingDisclosurePopup(): void {
    this.analyticsService.trackGoal(97);

    window.open(`${this.wwwUrl}/licensing_disclosure.php`, 'name', 'scrollbars=1,height=500,width=500');
  }

  getZipDetails(zipCode: string, type: string, creditRating: string): Observable<GetLocationResponse> {
    const qs = new HttpParams()
      .set('zip', zipCode)
      // FRU-21526: the /status/location endpoint only accepts refinance or purchase. Every home-equity
      // sub-type (heloc, cashout, homeequity, homeimprovement, 2ndmortgage) and personalloan is a
      // refinance-flow product, so normalize anything that is not purchase to refinance — otherwise the
      // lookup 404s and city/state come back null (empty city/zip risks losing the lead at create).
      .set('type', type === 'purchase' ? 'purchase' : 'refinance')
      .set('credit', creditRating === 'Unknown' ? 'good' : creditRating.toLowerCase());

    const url = `${this.apiUrl}/status/location?${qs}`;

    return this.http.get<GetLocationResponse>(url).pipe(
      take(1),
      catchError(x => {
        // FRU-23360: notFound tells a ZIP the API does not know apart from a failed request, so Hope can ask the
        // visitor to check the digits rather than to retry. It matches LocationStatusController's exact unknown-ZIP
        // body; its other 404s ("Could not find State for zipcode: N", invalid request) stay ordinary failures. The
        // original form reads only state and city from this object, so the extra field changes nothing there.
        const notFound = isZipNotFoundError(x);
        return of({ state:null, city: null, enabled: null,  lenderAvailable: null, notFound})
      }));
  }

  /**
   * Redirect consumer to rate tables page
   */
  // redirectRateTables(leadForm: UntypedFormGroup): void {
  //  let params = new HttpParams()
  //    .set('type', leadForm.controls.type && leadForm.controls.type.value || '')
  //    .set('state', leadForm.controls.propertyState && leadForm.controls.propertyState.value || '');
  //  const path = '/mortgage-refinance-purchase-fha-va-jumbo-offers';
  //  this.analyticsService.trackRedirect(path);
  //  document.location.href = path + '?' + params.toString();
  // }

  /**
   * Redirect consumer to other rate tables page
   */
  redirectOffers(leadForm: Lead) {
    let params = new HttpParams();
    if (typeof leadForm !== 'undefined') {
      /*If we’re redirecting a poor credit, but they had set a cash out of 1,000 - 50,000,
      mortgage-refinance-offers?personal-loan=1&cash_out=###&loan_amount=###&property_type=###&loan_type={refinance||purchase} */
      if (leadForm.cashOut >= 1000) {
        params = params
          .set('personal-loan', '1')
          .set('cash_out', leadForm.cashOut?.toString() || '')
          .set('loan_amount', leadForm.desiredLoanAmount?.toString() || '')
          .set('property_type', leadForm.propertyType || '')
          .set('loan_type', leadForm.type || '')
          .set('credit_rating', leadForm.creditRating || '')
          .set('is_own', leadForm.housingSituation || '0');
      } else {
        params = params
          .set('type', leadForm.type || '')
          .set('cash_out', leadForm.cashOut?.toString() || '')
          .set('state', leadForm.propertyState || '');
      }
    }
    params.set('fru_source', 'mortgage-refinance-offers');

    const path = '/mortgage-refinance-offers';
    this.analyticsService.trackRedirect(path);
    document.location.href = path + '?' + params.toString();
  }

  /**
   * Redirect consumer to other rate tables page
   */
  redirectOffersPurchase(leadForm: Lead) {
    let params = new HttpParams();
    if (typeof leadForm !== 'undefined') {
      params = params.set('lead_id', leadForm.leadId || '');
    }
    const path = '/mortgage-purchase-offers'
    this.analyticsService.trackRedirect(path);
    document.location.href = path + '?' + params.toString();
  }

  getState() {
    const url: string = `${this.adminUrl}/public_api/ip_state`
    return new Promise((resolve, reject) => {
      this.http.get(url).toPromise()
        .then((body) => { resolve(body['state'] || ''); })
        .catch(() => { resolve(''); })
    });
  }

  getStateNew() {
    const url: string = `${this.adminUrl}/public_api/ip_state`;
    return this.http.get<any>(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getLandingPageState() {
    const url: string = `${this.adminUrl}/public_api/ip_state`;
    return this.http.get(url).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  /**
   * Check Neustar score
   */

   getNeustarScore(lead: PostLead): Observable<NeustarScore> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/lead_manager/get_neustar_score`;

    return this.http.post<NeustarScore>(url, lead, httpOptions).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  /**
   * getConsumersReviews for ab test
   */

  getConsumersReviews(id) {
    const url: string = `${this.adminUrl}/public_api/get_top_consumer_reviews/${id}`
    return this.http.get<ConsumerReviewsResponse>(url).pipe(
      map(x => x.data),
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getStatesList() {
    const url: string = `${this.adminUrl}/public_api/get_states_list`
    return this.http.get<{ status: boolean, data: State[] }>(url).pipe(
      map(x => x.data),
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  getStateRateDescription(state: string): Observable<StateRateDescription | null> {

    const url = `${this.apiUrl}/state_rate_description/${state}`;
    return this.http.get<StateRateDescription>(url).pipe(
      catchError(x => {
        console.log('%c State Rate Description Error', 'color:aqua', x);
        return of(null);
      }))
  }

  getPageInfo(page = 'default'): Observable<PageInfo> {
    const url = `${this.apiUrl}/product_info/${page}`;
    return this.http.get<PageInfo>(url).pipe(
      catchError(x => {
        return of(null);
      }))
  }

  effectiveAdvertisedRatesEnabled(): boolean {
    return this.appConfig.effectiveAdvertisedRatesEnabled;
  }

  rememberEffectiveAdvertisedRateProgram(program: any): void {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled) { return; }
    const requested = program == null ? '' : String(program).toLowerCase().trim();
    try { (window as any).fruEffectiveAdvertisedRateProgram = requested; } catch (e) { /* non-fatal */ }
  }

  effectiveAdvertisedRateProgram(): string {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled) { return ''; }
    try {
      const requested = (window as any).fruEffectiveAdvertisedRateProgram;
      return typeof requested === 'string' ? requested : '';
    } catch (e) {
      return '';
    }
  }

  // FRU-24222: the scope also records how its answer is validated ('' for every caller, 'line_of_credit' for Hope's HELOC
  // entries), so an answer validated one way never becomes the current rate for a page that asked the other way.
  private activateEffectiveAdvertisedRateScope(key: string | null, policy = ''): void {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled) { return; }
    try {
      (window as any).fruActiveEffectiveAdvertisedRateKey = key;
      (window as any).fruActiveEffectiveAdvertisedRatePolicy = policy;
      (window as any).fruCurrentEffectiveAdvertisedRate = null;
    } catch (e) { /* non-fatal */ }
  }

  clearEffectiveAdvertisedRateScope(): void {
    this.activateEffectiveAdvertisedRateScope(null);
  }

  private rememberEffectiveAdvertisedRate(key: string, rate: Rate | null, policy = ''): void {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled) { return; }
    try {
      if ((window as any).fruActiveEffectiveAdvertisedRateKey === key
        && ((window as any).fruActiveEffectiveAdvertisedRatePolicy || '') === policy) {
        (window as any).fruCurrentEffectiveAdvertisedRate = rate && rate.status === 'ok' ? rate : null;
      }
    } catch (e) { /* non-fatal */ }
  }

  currentEffectiveAdvertisedRate(): Rate | null {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled) { return null; }
    try {
      const rate = (window as any).fruCurrentEffectiveAdvertisedRate as Rate;
      const activeKey = (window as any).fruActiveEffectiveAdvertisedRateKey;
      return rate && rate.purpose && rate.product && rateBucketKey({
        purpose: rate.purpose,
        product: rate.product,
      }) === activeKey ? rate : null;
    }
    catch (e) { return null; }
  }

  hasCurrentEffectiveAdvertisedRate(): boolean {
    return this.currentEffectiveAdvertisedRate() !== null;
  }

  // FRU-21810 perf: cached so a direct-Hope entry can prime this during bootstrap and the step
  // components (loan-purpose / property-type -- the only consumers) read the cached value instead
  // of paying a fresh ~250ms round trip at t~2.2s. Adversarial review 7/27: BOTH the value cache
  // AND the in-flight observable must live on window, not on the instance -- FruHttpService is
  // provided separately by the root module and the lazy assistant module, so the step components
  // run on a DIFFERENT instance than the one the rates-form prime used, and the step's call lands
  // WHILE the prime's request is still in flight (measured: 2 boot requests with an instance-level
  // share). Sharing the replayed observable itself dedupes the in-flight case across instances
  // (same pattern family as ensureUiConfig below). On error the shared observable is cleared so
  // the next subscriber refetches; the current subscriber still gets null exactly as before.
  // Ed 8/4: the canonical bucket for genuinely unscoped/default openings. FRU-22065 narrows this
  // policy: an explicit product request never borrows this bucket when its own result is unavailable.
  // Ed 8/9 10:57 PM ("Rates not matching what's in the ads is an absolute conversion killer"):
  // the DEFAULT anchor must be the SAME as-low-as pair the default ads advertise — the BEST
  // refinance bucket (10yr fixed, 4.8% 5.3 APR in the current reviewed revision). The 15yr anchor
  // showed 5.1% 5.4 APR to every generic-keyword click while the Mortgage_Rates_Today ads promise
  // 4.8/5.3 (product-seeded presentations already anchor best-refi and matched). Both pairs come
  // from the same reviewed effective revision — this is an anchor choice, not a rate edit.
  static readonly DEFAULT_RATE_BUCKET: AdvertisedRateBucket = { purpose: 'refinance', product: '10yr_fixed' };

  // FRU-23389: a reviewed rate for a bucket WITHOUT taking over the page's rate scope. The calculator-landing payments test
  // prices the visitor's payment on the 30-year fixed pair while the landing keeps showing its own headline rate, so this
  // never activates or remembers a scope, and keeps its own window cache. Same payload validation as getRateFinderRates;
  // anything unavailable comes back as an unavailable Rate, never another bucket's.
  peekRateFinderRates(bucket: AdvertisedRateBucket): Observable<Rate> {
    if (!this.appConfig.effectiveAdvertisedRatesEnabled || !bucket) { return of(unavailableEffectiveRate(bucket, 'effective_rates_disabled')); }
    if (!this.apiUrl || !this.apiUrl.trim()) { return of(unavailableEffectiveRate(bucket, 'missing_disclosure_origin')); }
    let w: any = null;
    try { w = window as any; } catch (e) { w = null; }
    const key = rateBucketKey(bucket);
    try {
      if (w) { w.fruPeekRateFinderRates = w.fruPeekRateFinderRates || {}; }
      if (w && w.fruPeekRateFinderRates[key]) { return of(w.fruPeekRateFinderRates[key] as Rate); }
    } catch (e) { /* non-fatal */ }
    // FRU-23774: a request already on its way is shared, so the calculator that mounts during the startup warm-up's request
    // waits for that answer instead of asking again. Like getRateFinderRates, it lives on window: the warm-up and the
    // calculator run on different FruHttpService instances.
    try {
      if (w) { w.fruPeekRateFinderRatesInflight = w.fruPeekRateFinderRatesInflight || {}; }
      if (w && w.fruPeekRateFinderRatesInflight[key]) { return w.fruPeekRateFinderRatesInflight[key] as Observable<Rate>; }
    } catch (e) { /* non-fatal */ }
    const params = new HttpParams().set('purpose', bucket.purpose).set('product', bucket.product);
    const request$ = this.http.get<any>(`${this.apiUrl}/rate_finder_rates`, { params }).pipe(
      map(payload => validatedEffectiveRate(payload, bucket, this.apiUrl)
        || unavailableEffectiveRate(bucket, payload && payload.reason ? String(payload.reason) : 'invalid_effective_payload')),
      tap(result => { try { if (w && result && result.status === 'ok') { w.fruPeekRateFinderRates[key] = result; } } catch (e) { /* non-fatal */ } }),
      catchError(() => of(unavailableEffectiveRate(bucket, 'fetch_failed'))),
      finalize(() => { try { if (w && w.fruPeekRateFinderRatesInflight) { delete w.fruPeekRateFinderRatesInflight[key]; } } catch (e) { /* non-fatal */ } }),
      shareReplay({ bufferSize: 1, refCount: false }));
    try { if (w) { w.fruPeekRateFinderRatesInflight[key] = request$; } } catch (e) { /* non-fatal */ }
    return request$;
  }

  // FRU-24222: opts (lineOfCredit) is passed only by Hope for its HELOC entries; every other call validates as before.
  getRateFinderRates(bucket?: AdvertisedRateBucket, opts?: EffectiveRateValidation): Observable<Rate> {
    let w: any = null;
    try { w = window as any; } catch (e) { w = null; }
    if (this.appConfig.effectiveAdvertisedRatesEnabled) {
      if (!bucket) {
        // Ed 8/4 P0: an unscoped surface (the DEFAULT landing -- the highest-traffic pages) must
        // never render rate-less. Fall back to the canonical default bucket, whose display matches
        // what the legacy SiteSettings figure showed (refi 15yr fixed). The scoped machinery below
        // then keeps the number<->disclosure pairing intact for the bucket we actually show.
        return opts ? this.getRateFinderRates(FruHttpService.DEFAULT_RATE_BUCKET, opts) : this.getRateFinderRates(FruHttpService.DEFAULT_RATE_BUCKET);
      }
      const key = rateBucketKey(bucket);
      // A line-of-credit answer is validated differently, so it is cached, shared in flight and made current apart from the
      // same bucket asked without it.
      const policy = opts && opts.lineOfCredit ? 'line_of_credit' : '';
      const cacheKey = policy ? `${key}|${policy}` : key;
      this.activateEffectiveAdvertisedRateScope(key, policy);
      if (!this.apiUrl || !this.apiUrl.trim()) {
        return of(unavailableEffectiveRate(bucket, 'missing_disclosure_origin'));
      }
      if (w) {
        w.fruEffectiveRateFinderRates = w.fruEffectiveRateFinderRates || {};
        w.fruEffectiveRateFinderRates$ = w.fruEffectiveRateFinderRates$ || {};
        if (w.fruEffectiveRateFinderRates[cacheKey]) {
          const cached = w.fruEffectiveRateFinderRates[cacheKey] as Rate;
          this.rememberEffectiveAdvertisedRate(key, cached, policy);
          return of(cached);
        }
        if (w.fruEffectiveRateFinderRates$[cacheKey]) { return w.fruEffectiveRateFinderRates$[cacheKey] as Observable<Rate>; }
      }
      const params = new HttpParams().set('purpose', bucket.purpose).set('product', bucket.product);
      // FRU-23820: Hope's loading card asks for the landing's rate as the page starts (index.html), about half a second
      // before the app could; that answer is used once here, and a failed or empty one falls back to asking again.
      const ask = () => this.http.get<any>(`${this.apiUrl}/rate_finder_rates`, { params });
      let source$: Observable<any> | null = null;
      try {
        // FRU-24222: the loading card's heloc.heloc request is Hope's line-of-credit one, so only that caller takes it.
        const prefetchFits = !(bucket.purpose === 'heloc' && bucket.product === 'heloc') || policy === 'line_of_credit';
        const early: any = prefetchFits && w && w.__hopeRatePrefetch ? w.__hopeRatePrefetch[key] : null;
        if (early && typeof early.then === 'function') {
          delete w.__hopeRatePrefetch[key];
          source$ = from(early as Promise<any>).pipe(switchMap((payload: any) => payload ? of(payload) : ask()));
        }
      } catch (e) { /* the normal request below */ }
      const req$ = (source$ || ask()).pipe(
        map(payload => edofficeLegacyRatePreview(payload, this.wwwUrl)
          || validatedEffectiveRate(payload, bucket, this.apiUrl, opts)
          || unavailableEffectiveRate(bucket, payload && payload.reason ? String(payload.reason) : 'invalid_effective_payload')),
        tap(result => {
          try {
            if (!w) { return; }
            if (result.status === 'ok') { w.fruEffectiveRateFinderRates[cacheKey] = result; }
            else { w.fruEffectiveRateFinderRates$[cacheKey] = null; }
          } catch (e) { /* non-fatal */ }
        }),
        map(result => {
          // A response for a scope the visitor has already left may still be
          // cached for a later return, but it must never reach the current UI.
          // Otherwise late A can paint a numeric claim while the disclosure
          // pointer already belongs to B.
          try {
            if (!w || w.fruActiveEffectiveAdvertisedRateKey !== key || (w.fruActiveEffectiveAdvertisedRatePolicy || '') !== policy) {
              return unavailableEffectiveRate(bucket, 'stale_product_scope');
            }
          } catch (e) {
            return unavailableEffectiveRate(bucket, 'stale_product_scope');
          }
          this.rememberEffectiveAdvertisedRate(key, result, policy);
          return result;
        }),
        catchError(() => {
          console.error('error in scoped /rate_finder_rates');
          this.rememberEffectiveAdvertisedRate(key, null, policy);
          try { if (w) { w.fruEffectiveRateFinderRates$[cacheKey] = null; } } catch (e) { /* non-fatal */ }
          return of(unavailableEffectiveRate(bucket, 'fetch_failed'));
        }),
        shareReplay({ bufferSize: 1, refCount: false }));
      if (w) { try { w.fruEffectiveRateFinderRates$[cacheKey] = req$; } catch (e) { /* non-fatal */ } }
      // FRU-22065: a supplied bucket is an exact product contract. Missing, malformed, failed, or
      // stale results remain unavailable for that same bucket; never substitute refi/15yr (or any
      // other valid but unrelated product). The no-argument path above preserves generic UX.
      return req$;
    }

    // Exact rollback path: no query parameters and the original single global
    // value/in-flight caches. The optional scope is deliberately ignored.
    if (w && w.fruRateFinderRates) { return of(w.fruRateFinderRates as Rate); }
    if (w && w.fruRateFinderRates$) { return w.fruRateFinderRates$ as Observable<Rate>; }
    const req$ = this.http.get<Rate>(`${this.apiUrl}/rate_finder_rates`).pipe(
      tap(r => { try { if (r && w) { w.fruRateFinderRates = r; } } catch (e) { /* non-fatal */ } }),
      catchError(x => {
        console.error('error in /rate_finder_rates');
        try { if (w) { w.fruRateFinderRates$ = null; } } catch (e) { /* non-fatal */ }
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }));
    if (w) { try { w.fruRateFinderRates$ = req$; } catch (e) { /* non-fatal */ } }
    return req$;
  }

  getPolicyHtml(key: string = "privacy_policy_html", siteId: number = 1): Observable<string> {
    const url: string = `${this.adminUrl}/public_api/get_site_content/${key}/${siteId}`;
    return this.http.get(url, { responseType: 'text' }).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  doNotSell(formData: any): Observable<any> {

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }

    let params = new HttpParams();
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        params = params.set(key, formData[key]);
      }
    }

    const url: string = `${this.adminUrl}/public_api/do_not_sell_ng`;
    return this.http.post<{ status: string, message: string }>(url, params.toString(), httpOptions).pipe(
      catchError((err) => {
        if (err.status === 0) return EMPTY;
        throw err;
      })
    );
  }

  // FastRate Property Lookup (Phase-3): server-side proxied REAPI. No key in the bundle.
  // HMAC option 1 (Denis 7/10): form_landing inlines the signed header pairs, but a DIRECT
  // /communication/rates-form entry renders no PHP -- so when neither window nor sessionStorage has
  // them, fetch the pair once from the same-origin fru endpoint and stash them where
  // fastRateHttpOptions() reads. Fire-and-forget: an early unsigned call degrades gracefully
  // (backend rejects -> empty suggestions) and the next keystroke goes out signed.
  // FRU-21526 (Ed 7/10): live-tunable UI config -- fetch once when the landing didn't inject it.
  private uiConfigRequested = false;
  // FRU-24172: every settings read calls this while the config is missing (about every 150 ms), so a failed request waits
  // before the next one: 1, 2, 4, 8 and 16 s, then no more after six. The shipped defaults stand in meanwhile.
  private uiConfigFailures = 0;
  private uiConfigRetryAt = 0;
  ensureUiConfig(): void {
    try {
      const w: any = window as any;
      if (w.fruUiConfig) { return; }
      try {
        const stored = JSON.parse(window.sessionStorage.getItem('fruUiConfig') || 'null');
        if (stored && typeof stored === 'object') { w.fruUiConfig = stored; return; }
      } catch (e) { /* refetch below */ }
      if (this.uiConfigRequested || w.fruUiConfigInFlight) { return; }
      if (this.uiConfigFailures >= 6 || Date.now() < this.uiConfigRetryAt) { return; }
      this.uiConfigRequested = true;
      w.fruUiConfigInFlight = true;
      this.http.get(`${this.apiUrl}/consumer_chat/ui_config`).pipe(catchError(() => of(null))).subscribe((resp: any) => {
        if (resp && typeof resp === 'object') {
          w.fruUiConfig = resp;
          try { window.sessionStorage.setItem('fruUiConfig', JSON.stringify(resp)); } catch (e) { /* non-fatal */ }
          // Codex 7/10 (#7): timers scheduled before this response used the shipped defaults --
          // announce arrival so live timers can re-arm with the configured values.
          try { window.dispatchEvent(new CustomEvent('fru-ui-config-loaded')); } catch (e) { /* non-fatal */ }
        } else {
          this.uiConfigFailures++;
          this.uiConfigRetryAt = Date.now() + 1000 * Math.pow(2, this.uiConfigFailures - 1);
          this.uiConfigRequested = false;
        }
        try { w.fruUiConfigInFlight = false; } catch (e) { /* non-fatal */ }
      });
    } catch (e) { /* non-fatal */ }
  }

  private fastRateHeadersRequested = false;
  private hasFastRateHeaders(headers: any): boolean {
    return !!(headers && headers.addressAutocomplete && headers.propertyLookup);
  }

  ensureFastRateHeaders(): void {
    try {
      const w: any = window as any;
      if (this.hasFastRateHeaders(w.fru21526FastRateApiHeaders)) { return; }
      w.fru21526FastRateApiHeaders = null;
      try {
        const stored = JSON.parse(window.sessionStorage.getItem('fru21526FastRateApiHeaders') || 'null');
        if (this.hasFastRateHeaders(stored)) { w.fru21526FastRateApiHeaders = stored; return; }
        window.sessionStorage.removeItem('fru21526FastRateApiHeaders');
      } catch (e) { /* malformed -- refetch below */ }
      if (this.fastRateHeadersRequested) { return; }
      this.fastRateHeadersRequested = true;
      this.http.get('/ng_api/fastrate_headers').pipe(catchError(() => of(null))).subscribe((resp: any) => {
        if (this.hasFastRateHeaders(resp)) {
          w.fru21526FastRateApiHeaders = resp;
          try { window.sessionStorage.setItem('fru21526FastRateApiHeaders', JSON.stringify(resp)); } catch (e) { /* non-fatal */ }
        } else {
          this.fastRateHeadersRequested = false;   // endpoint missing/erroring -- allow a later retry
        }
      });
    } catch (e) { /* non-fatal */ }
  }

  private fastRateHttpOptions(key: 'addressAutocomplete' | 'propertyLookup'): { headers?: HttpHeaders } {
    let headers = (window as any).fru21526FastRateApiHeaders && (window as any).fru21526FastRateApiHeaders[key];
    if (!headers) {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem('fru21526FastRateApiHeaders') || '{}');
        headers = stored && stored[key];
      } catch (e) {}
    }

    return headers ? { headers: new HttpHeaders(headers) } : {};
  }

  addressAutocomplete(search: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/consumer_chat/address_autocomplete`, { search }, this.fastRateHttpOptions('addressAutocomplete')).pipe(
      catchError(() => of({ success: false, suggestions: [] }))
    );
  }
  // FRU-21526 HOPE 2.0 (Ed 7/20): returning-visitor recognition (laravel #1250). The server returns
  // MASKED data only (phone last-4, masked email) -- raw contact never reaches the browser. Any
  // failure (404 before #1250 deploys, network, throttle) quietly reads as not-found.
  returningLookup(vid: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/consumer_chat/returning`, { vid }).pipe(
      // FRU-23530: a failed lookup is not an answer. Flag it, so the chat can tell "not found" from "could not ask".
      catchError(() => of({ found: false, lookupFailed: true }))
    );
  }
  // FRU-21566 Phase 2 (Ed 7/25): phone-match ownership proof. The typed phone (fresh TCPA anyway)
  // unlocks the full saved lead for the one-tap returning submit. Transport failures must remain
  // errors so Chat's owned claim FSM can unlock Phone for a retry; `{found:false}` is reserved for
  // a real server mismatch and intentionally falls back to the review walk.
  returningClaim(vid: string, phone: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/consumer_chat/returning/claim`, { vid, phone });
  }
  propertyLookup(id: string, threadid?: string | null): Observable<any> {
    return this.http.post(`${this.apiUrl}/consumer_chat/property_lookup`, { id, threadid: threadid || undefined }, this.fastRateHttpOptions('propertyLookup')).pipe(
      catchError(() => of({ success: false, property: null }))
    );
  }

  saveChatResponse(text: string, step: string, threadId?: string, form_data?: any): Observable<any> {
    let post: any = { message: text, step: step, conversation_context: this.rateFinderConversationContext() };
    if (threadId) {
      post['threadid'] = threadId;
    }
    if (form_data) {
      post['form_data'] = form_data;
    }
    return this.http.post(`${this.apiUrl}/consumer_chat/form`, post).pipe(
      tap(() => this.hopeApiLog('saveChatResponse', true, 200)),
      catchError((error) => {
        this.hopeApiLog('saveChatResponse', false, error && error.status);
        captureRateFinderRequestFailure(error, 'assistant_response_failure', {
          api: 'saveChatResponse',
          http_status: error && error.status,
        });
        console.error('FruHttpService.saveChatResponse failed', error);
        if (error.status === 0) return EMPTY;
        throw error;
      })
    );
  }

  // FRU-21633: in-page telemetry for the Hope API — records every consumer_chat/form call + status to
  // window.__hopeEvents (DevTools-inspectable) so failures (e.g. the No-conversation-found 404) and
  // successful AI answers are visible in real time, and it is provable the backend IS being called.
  private hopeApiLog(api: string, ok: boolean, status?: number): void {
    try {
      const w: any = window; w.__hopeEvents = w.__hopeEvents || [];
      w.__hopeEvents.push({ kind: ok ? 'api_ok' : 'api_error', api, status: (status === undefined ? null : status), ts: new Date().toISOString() });
      if (w.__hopeEvents.length > 300) { w.__hopeEvents.shift(); }
      if (!ok) { console.warn('[HopeEvent] api_error', api, status); }
    } catch (e) { /* telemetry never breaks the app */ }
  }

  private actionPost(chatAction: {action: string, step: string, value: string, message: string, meta?: any}, threadId?: string): any {
    let post: any = {
      action: chatAction.action,
      step: chatAction.step,
      value: chatAction.value,
      message: chatAction.message,
      conversation_context: this.rateFinderConversationContext()
    };
    if (chatAction.meta && Object.keys(chatAction.meta).length) {
      post['meta'] = chatAction.meta;
    }
    if (threadId) {
      post['threadid'] = threadId;
    }
    return post;
  }

  saveAction(chatAction: {action: string, step: string, value: string, message: string, meta?: any}, threadId?: string): Observable<any> {
    // FRU-23258: an action sent while the page is going (hidden, pagehide, freeze, the leave prompt) would be cancelled with
    // it. Once the thread exists, send it as a keepalive request, which the browser finishes after the page is gone. A
    // browser Back alone doesn't count: the page usually stays, and this save keeps its normal request, error reporting and
    // place in the queue. Without a thread the normal request has to run, because its answer carries the new thread id.
    if (threadId && pageDepartingSince(Date.now() - 1000) && this.saveActionOnUnload(chatAction, threadId)) {
      return of(null);
    }
    const post = this.actionPost(chatAction, threadId);
    // FRU-24080: a post that got no answer (status 0) is sent again once the page is visible and online, twice at most. If the
    // page goes while it waits, the post goes on the keepalive request when the thread exists; one away for ten minutes is
    // given up quietly. A real HTTP error isn't retried.
    let retries = 0;
    return this.http.post(`${this.apiUrl}/consumer_chat/form`, post).pipe(
      retry({
        count: ACTION_RETRY_DELAYS_MS.length,
        delay: (error, attempt) => {
          if (!error || error.status !== 0) { return throwError(() => error); }
          retries = attempt;
          addRateFinderBreadcrumb('action_retry', { api: 'saveAction', attempt, action: chatAction.action, step: chatAction.step });
          return actionRetryNotifier(attempt, () => { if (threadId) { this.saveActionOnUnload(chatAction, threadId); } });
        },
      }),
      tap(() => this.hopeApiLog('saveAction', true, 200)),
      catchError((error) => {
        this.hopeApiLog('saveAction', false, error && error.status);
        // FRU-24201 (ANGULAR-7F7): still no answer after the resends means the phone's connection is down. Keep the post for
        // the tab and send it when the connection comes back; it's reported only if that fails too. Without a thread the post
        // can't be kept: its answer would carry the new thread id, so it's reported as before.
        if (error && error.status === 0 && threadId && this.keepUnsentAction(post, retries)) {
          return EMPTY;
        }
        captureRateFinderRequestFailure(error, 'assistant_action_failure', {
          api: 'saveAction',
          http_status: error && error.status,
          retries,
        });
        console.error('FruHttpService.saveAction failed', error);
        if (error.status === 0) return EMPTY;
        throw error;
      })
    );
  }

  /** FRU-24201: keep a step post that got no answer. False when the tab can't keep it (no usable sessionStorage). */
  private keepUnsentAction(post: any, retries: number): boolean {
    if (!addToOutbox(defaultOutboxStore(), post, Date.now())) { return false; }
    addRateFinderBreadcrumb('action_kept', { api: 'saveAction', retries, step: post && post.step });
    this.wireActionOutbox();
    return true;
  }

  /** FRU-24201: on a page load with posts kept from an earlier page in the tab, listen and send them. Nothing otherwise. */
  private resumeActionOutbox(): void {
    try {
      if (!readOutbox(defaultOutboxStore()).length) { return; }
      this.wireActionOutbox();
      setTimeout(() => this.flushActionOutbox(), 0);
    } catch (e) { /* best effort */ }
  }

  private wireActionOutbox(): void {
    const { doc, win } = actionRetryEnv();
    if (actionOutboxGuards.wired || !win || !doc) { return; }
    actionOutboxGuards.wired = true;
    const flush = () => this.flushActionOutbox();
    win.addEventListener('online', flush);
    win.addEventListener('pageshow', flush);
    doc.addEventListener('visibilitychange', () => { if (doc.visibilityState !== 'hidden') { flush(); } });
    // Leaving the tab: the kept posts go on keepalive requests, which the browser finishes after the page is gone.
    win.addEventListener('pagehide', () => this.sendActionOutboxOnUnload());
  }

  private flushActionOutbox(): void {
    const store = defaultOutboxStore();
    if (!store || actionOutboxGuards.flushing || !readOutbox(store).length) { return; }
    const { doc, nav } = actionRetryEnv();
    if (nav && nav.onLine === false) { return; }
    if (doc && doc.visibilityState === 'hidden') { return; }
    actionOutboxGuards.flushing = true;
    flushOutbox(store, (post) => this.sendKeptAction(post), Date.now()).then((result) => {
      actionOutboxGuards.flushing = false;
      if (result.sent) { addRateFinderBreadcrumb('action_kept_sent', { api: 'saveAction', sent: result.sent, kept: result.kept }); }
      for (const item of result.gaveUp) {
        captureRateFinderRequestFailure({ status: 0 }, 'assistant_action_failure', {
          api: 'saveAction', http_status: 0, kept: true, tries: item.tries, waited_ms: Date.now() - item.queuedAt,
          ...(result.saved ? {} : { store_refused: true }),
        });
      }
      for (const { item, status } of result.rejected) {
        captureRateFinderRequestFailure({ status }, 'assistant_action_failure', {
          api: 'saveAction', http_status: status, kept: true, tries: item.tries,
        });
      }
    }, () => { actionOutboxGuards.flushing = false; });
  }

  /** One send of a kept post, resolving its HTTP status (0 when it got no answer). */
  private sendKeptAction(post: any): Promise<number> {
    return new Promise((resolve) => {
      this.http.post(`${this.apiUrl}/consumer_chat/form`, post).subscribe({
        next: () => resolve(200),
        error: (error) => resolve(error && typeof error.status === 'number' ? error.status : 0),
      });
    });
  }

  private sendActionOutboxOnUnload(): void {
    // A flush in progress is already sending these; sending them again here would post them twice. They stay kept.
    if (actionOutboxGuards.flushing) { return; }
    const store = defaultOutboxStore();
    const items = readOutbox(store);
    if (!items.length) { return; }
    try {
      const browserFetch = (window as any).fetch;
      if (typeof browserFetch !== 'function') { return; }
      // Each post is dropped only once its keepalive request is answered with success. One that fails, or whose answer never
      // arrives because the page is gone first, stays kept for the next page in the tab; if it did reach the server, that
      // resend is the duplicate meta.resent marks.
      for (const item of items) {
        const request = browserFetch.call(window, `${this.apiUrl}/consumer_chat/form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.post),
          credentials: 'omit',
          keepalive: true,
        });
        if (request && typeof request.then === 'function') {
          request.then((response: any) => { if (response && response.ok) { removeFromOutbox(store, item); } }, () => { /* stays kept */ });
        }
      }
      addRateFinderBreadcrumb('action_kept_unload', { api: 'saveAction', sent: items.length });
    } catch (e) { /* best effort: the posts stay kept for the next page in the tab */ }
  }

  saveActionOnUnload(chatAction: {action: string, step: string, value: string, message: string, meta?: any}, threadId?: string): boolean {
    try {
      const browserFetch = (window as any).fetch;
      if (typeof browserFetch !== 'function') { return false; }

      const request = browserFetch.call(window, `${this.apiUrl}/consumer_chat/form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.actionPost(chatAction, threadId)),
        credentials: 'omit',
        keepalive: true,
      });
      if (request && typeof request.catch === 'function') {
        request.catch((error) => {
          this.hopeApiLog('saveActionOnUnload', false, error && error.status);
          console.error('FruHttpService.saveActionOnUnload failed', error);
        });
      }
      return true;
    } catch (error) {
      this.hopeApiLog('saveActionOnUnload', false, error && error.status);
      return false;
    }
  }

  // FRU-21494: one first-party identifier joins the classic form, Hope actions and the final lead.
  // Tracking opt-out deliberately returns no identifier and never writes a cookie.
  getRateFinderSessionId(): string {
    const cookies = parseConversationCookies(document.cookie || '');
    if (Object.prototype.hasOwnProperty.call(cookies, 'fruRemoveCookies')) { return ''; }

    let sessionId = cookies['rate_finder_session_id'] || '';
    if (sessionId) { return sessionId; }

    try {
      const browserCrypto: any = (window as any).crypto;
      sessionId = (browserCrypto && browserCrypto.randomUUID)
        ? browserCrypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
            const n = Math.floor(Math.random() * 16);
            return (ch === 'x' ? n : ((n & 0x3) | 0x8)).toString(16);
          });
      const expires = new Date(Date.now() + 86400000).toUTCString();
      const host = window.location.hostname.split('.');
      const domain = host.length > 1 ? '; domain=.' + host.slice(-2).join('.') : '';
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = 'rate_finder_session_id=' + encodeURIComponent(sessionId)
        + '; expires=' + expires + '; path=/' + domain + '; SameSite=Lax' + secure;
    } catch (e) { sessionId = ''; }

    return sessionId;
  }

  rateFinderConversationContext(): any {
    const cookies = parseConversationCookies(document.cookie || '');
    const optedOut = Object.prototype.hasOwnProperty.call(cookies, 'fruRemoveCookies');
    const currentUrl = sanitizeConversationUrl(window.location.href, !optedOut);
    let landingUrl = currentUrl;
    try {
      const key = 'fru_rate_finder_landing_url';
      const stored = window.sessionStorage.getItem(key) || '';
      landingUrl = sanitizeConversationUrl(stored, !optedOut) || currentUrl;
      // Also cleans a raw value left in this tab by an earlier client build.
      if (landingUrl && stored !== landingUrl) { window.sessionStorage.setItem(key, landingUrl); }
    } catch (e) { /* session storage is optional */ }

    const sessionId = optedOut ? '' : this.getRateFinderSessionId();
    let retainedAdAttribution: any;
    if (!optedOut) {
      try { retainedAdAttribution = JSON.parse(window.sessionStorage.getItem(AD_ATTRIBUTION_STORAGE_KEY) || 'null'); }
      catch (e) { /* only validated structured ad identifiers can be reused */ }
    }

    const context = buildConversationContext({
      href: window.location.href,
      referrer: document.referrer || '',
      cookie: document.cookie || '',
      landingUrl,
      rateFinderSessionId: sessionId,
      retainedAdAttribution,
      hopeMarkers: AnalyticsService.parseHopeGa4Markers(window.location.search, document.cookie || ''),
    });
    try {
      persistConversationAdAttribution(window.sessionStorage, context.ad_attribution);
    } catch (e) { /* storage failure must not prevent a conversation */ }
    return context;
  }

}
